'use client';

import { useState, useEffect, useRef } from 'react';

import { classNames } from '@/shared/lib/classNames/classNames';

export type TooltipPosition = 'bottom-center' | 'bottom-left';

type TooltipVariant = 'light' | 'dark';

interface TooltipedProps {
  children: React.ReactNode;
  tooltipContent: React.ReactNode;
  className?: string;
  tooltipClassName?: string;
  tooltipArrowClassName?: string;
  variant?: TooltipVariant;
  position?: TooltipPosition;
  id?: string;
}

export const Tooltiped = ({
  children,
  tooltipContent,
  className,
  tooltipClassName,
  tooltipArrowClassName,
  variant = 'dark',
  position = 'bottom-center',
  id,
}: TooltipedProps) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    checkIsMobile();

    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobile &&
        isTooltipVisible &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsTooltipVisible(false);
      }
    };

    if (isMobile && isTooltipVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isTooltipVisible]);

  const handleInteraction = () => {
    if (isMobile) {
      setIsTooltipVisible((prev) => !prev);
    } else {
      setIsTooltipVisible(true);
    }
  };

  const handleBlur = () => {
    if (!isMobile) {
      setIsTooltipVisible(false);
    }
  };

  return (
    <div
      id={id}
      ref={containerRef}
      className={classNames({ cls: 'relative', additional: [className] })}
      onMouseEnter={!isMobile ? handleInteraction : undefined}
      onMouseLeave={!isMobile ? handleBlur : undefined}
      onClick={isMobile ? handleInteraction : undefined}
      onBlur={handleBlur}
    >
      {children}

      {isTooltipVisible && (
        <Tooltip
          className={tooltipClassName}
          tooltipArrowClassName={tooltipArrowClassName}
          variant={variant}
          position={position}
        >
          {tooltipContent}
        </Tooltip>
      )}
    </div>
  );
};

const Tooltip = ({
  children,
  className,
  tooltipArrowClassName,
  variant,
  position,
}: {
  children: React.ReactNode;
  className?: string;
  tooltipArrowClassName?: string;
  variant?: TooltipVariant;
  position?: TooltipPosition;
}) => {
  return (
    <div
      className={classNames({
        cls: 'absolute top-full z-[2] mt-3 w-max max-w-80 transform rounded-[12px] p-4 pt-2 pb-2 whitespace-normal shadow-lg max-sm:max-w-[280px]',
        mods: {
          'bg-surface-base-tertiary': variant === 'dark',
          'text-text-base-primary': variant === 'dark',
          'bg-custom-base-primary': variant === 'light',
          'text-text-base-inverse-primary': variant === 'light',
          '-translate-x-1/2 max-sm:translate-x-[-50%]':
            position === 'bottom-center',
          'left-1/2': position === 'bottom-center',
          'left-0': position === 'bottom-left',
        },
        additional: [className],
      })}
    >
      <div className="text-sm leading-5 font-normal max-sm:text-xs">
        {children}
      </div>

      <div
        className={classNames({
          cls: 'absolute top-[-8px] h-0 w-0 -translate-x-1/2 transform border-r-[10px] border-b-[10px] border-l-[10px] border-r-transparent border-l-transparent',
          mods: {
            'border-b-surface-base-tertiary': variant === 'dark',
            'border-b-custom-base-primary': variant === 'light',
            'left-[32px]': position === 'bottom-left',
            'left-1/2': position === 'bottom-center',
          },
          additional: [tooltipArrowClassName],
        })}
      ></div>
    </div>
  );
};
